import { resolveAiText } from "@/lib/ai-text";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

const LONG_COMMENT_THRESHOLD = 220;

interface AiCommentBodyProps {
  text: string;
  showMoreLabel: string;
  showLessLabel: string;
  flag?: boolean;
}

export function AiCommentBody({
  text,
  showMoreLabel,
  showLessLabel,
  flag,
}: Readonly<AiCommentBodyProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Resolved before measuring: a sentinel is longer than the label it renders
  // to, so measuring the raw text would collapse a note that fits.
  const resolved = resolveAiText(text, t);
  const isLong = resolved.length > LONG_COMMENT_THRESHOLD;

  return (
    <div>
      <div className="relative">
        <div
          className={cn(
            "prose prose-sm max-w-none break-words text-sm leading-relaxed dark:prose-invert",
            "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
            flag
              ? "font-medium text-violet-900 dark:text-violet-200"
              : "text-gray-700 dark:text-gray-300",
            isLong && !expanded && "max-h-24 overflow-hidden",
          )}
        >
          <Markdown>{resolved}</Markdown>
        </div>
        {isLong && !expanded && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-popover to-transparent"
          />
        )}
      </div>
      {isLong && (
        <button
          type="button"
          aria-expanded={expanded}
          className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-violet-200 bg-violet-50/70 px-2 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/70"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
}
